import { fetchSuggestPrompts, useApiContext } from '@repo/api-codegen';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { AiChatStore } from '../store';
import { type PromptContextType } from '../utils';

/**
 * Determines if follow-up suggestions should be enabled based on the context.
 * Follow-up suggestions are enabled for bi_metric and bi_dashboard contexts only.
 *
 * @param contextType - The prompt context type
 * @returns true if follow-up suggestions should be enabled
 */
function shouldEnableFollowUpSuggestions(
	contextType: PromptContextType | null
): boolean {
	return contextType === 'bi_metric' || contextType === 'bi_dashboard';
}

/**
 * Fetches and returns AI prompt suggestions based on the store's context.
 * React Query automatically handles caching and deduplication, so we don't need
 * separate prefetching logic. The query will fetch when enabled conditions are met.
 *
 * Follow-up suggestions are automatically enabled for bi_metric and bi_dashboard contexts.
 *
 * @param store - The AI chat store
 * @returns Query result with data and loading state
 */
export function usePrefetchPromptSuggestions(store: AiChatStore) {
	const { fetcherOptions } = useApiContext();

	// Get context from store
	const { contextType, elementId } = store.promptContext;

	// Determine if follow-up suggestions should be enabled for this context
	const enableFollowUpSuggestions = useMemo(
		() => shouldEnableFollowUpSuggestions(contextType),
		[contextType]
	);

	// Construct query key and variables
	const queryKey = useMemo(
		() => ['suggestPrompts', contextType ?? 'onboarding', 3, elementId ?? null],
		[contextType, elementId]
	);

	const variables = useMemo(
		() => ({
			body: {
				context: (contextType ?? 'onboarding') as PromptContextType,
				num_prompts: 3,
				...(elementId ? { element_id: elementId } : {}),
			},
		}),
		[contextType, elementId]
	);

	// Compute enabled condition - React Query will automatically fetch when this becomes true
	const queryEnabled = useMemo(
		() =>
			enableFollowUpSuggestions &&
			store.canPrefetchSuggestions &&
			!!contextType &&
			!!elementId,
		[
			enableFollowUpSuggestions,
			store.canPrefetchSuggestions,
			contextType,
			elementId,
		]
	);
	// React Query handles caching, deduplication, and will fetch when enabled
	return useQuery({
		queryKey,
		queryFn: ({ signal }) =>
			fetchSuggestPrompts({ ...fetcherOptions, ...variables }, signal),
		staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
		refetchOnMount: enableFollowUpSuggestions, // Always refetch when query key changes (only if enabled)
		refetchOnWindowFocus: false, // Don't refetch when window regains focus
		enabled: queryEnabled,
	});
}
